import React, { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { enablePush, getPushStatus, isPushSupported, pushAvailability } from "@/lib/push";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

const STORAGE_KEY = "gv_push_banner_dismissed";

/**
 * Discreet banner inviting the logged-in user to enable push notifications.
 * Shown in-context (e.g. on /microlearning) — hidden once dismissed OR
 * once the user has subscribed. Dismissal persists in localStorage.
 */
export default function PushBanner({ compact = false }) {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [available, setAvailable] = useState(null); // null = unknown, false = no VAPID
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) === "1" : true
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported()) return;
    (async () => {
      const avail = await pushAvailability();
      setAvailable(avail.configured);
      if (avail.configured) {
        getPushStatus().then(setStatus).catch(() => {});
      }
    })();
  }, [user]);

  if (!user || !isPushSupported() || dismissed) return null;
  if (available === false) return null; // no VAPID → hide silently
  if (!status) return null;
  if (status.subscribed || status.permission === "denied") return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success(t("push.toastEnabled"));
      setStatus(await getPushStatus());
    } catch (e) {
      if (e?.code === "PUSH_NOT_CONFIGURED") {
        setAvailable(false);
      } else {
        toast.error(e?.message || t("push.toastError"));
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="push-banner"
      className={`relative rounded-2xl border border-[#E5276A]/25 bg-gradient-to-r from-[#FCE4ED]/60 to-white p-4 md:p-5 ${compact ? "" : "mb-6"}`}
    >
      <button
        onClick={dismiss}
        aria-label={t("common.cancel")}
        data-testid="push-banner-dismiss"
        className="absolute top-2 right-2 p-1 text-[#8B95A1] hover:text-[#E5276A]"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-[#E5276A] text-white flex items-center justify-center">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base md:text-lg text-[#1F3A5F] leading-tight">
            {t("pushBanner.title")}
          </div>
          <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">
            {t("pushBanner.body")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={activate}
              disabled={busy}
              data-testid="push-banner-activate"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661] disabled:opacity-60"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t("pushBanner.activate")}
            </button>
            <button
              onClick={() => navigate("/account")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#1F3A5F] hover:text-[#E5276A]"
            >
              {t("pushBanner.later")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
